import { useEffect, useState } from "react";
import { Users, Search, Shield, UserCheck, UserX, Trash2, Package, Wrench, Mail, Phone, Building2, FileDigit } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Admin", customer: "Cliente", prospect: "Prospect" };
const ROLE_CLASS = {
    admin: "bg-primary text-primary-foreground",
    customer: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    prospect: "bg-amber-100 text-amber-700 border border-amber-200",
};

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar utilizadores.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    async function openUser(uid) {
        setSelected(uid);
        setDetail(null);
        try {
            const { data } = await api.get(`/admin/users/${uid}`);
            setDetail(data);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar detalhe.");
        }
    }

    async function changeRole(uid, role) {
        try {
            await api.patch(`/admin/users/${uid}/role`, { role });
            toast.success("Papel actualizado");
            load();
            if (selected === uid) openUser(uid);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao actualizar papel.");
        }
    }

    async function deleteUser(uid) {
        if (!confirm("Eliminar este utilizador? Esta acção remove também os equipamentos e pedidos associados.")) return;
        try {
            await api.delete(`/admin/users/${uid}`);
            toast.success("Utilizador eliminado");
            if (selected === uid) { setSelected(null); setDetail(null); }
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao eliminar.");
        }
    }

    const filtered = users.filter((u) => {
        if (filter !== "all" && (u.role || "prospect") !== filter) return false;
        if (search && !`${u.email} ${u.name || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    return (
        <div data-testid="admin-users-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-extrabold text-primary flex items-center gap-2">
                        <Users className="h-6 w-6" /> Utilizadores
                    </h2>
                    <p className="text-sm text-muted-foreground">Gerir clientes, prospects e administradores.</p>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            data-testid="admin-users-search"
                            placeholder="Pesquisar por nome ou email..."
                            className="pl-9 pr-3 py-2 rounded-md border border-border bg-white text-sm w-64 outline-none focus:border-primary"
                        />
                    </div>
                    <select value={filter} onChange={(e) => setFilter(e.target.value)}
                        data-testid="admin-users-filter"
                        className="rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary">
                        <option value="all">Todos</option>
                        <option value="prospect">Prospects</option>
                        <option value="customer">Clientes</option>
                        <option value="admin">Admins</option>
                    </select>
                </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-5">
                <div className="lg:col-span-2 rounded-2xl border border-border bg-white overflow-hidden">
                    {loading ? (
                        <div className="p-6 sm:p-8 text-sm text-muted-foreground">A carregar...</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 sm:p-8 text-sm text-muted-foreground text-center">Sem resultados.</div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {filtered.map((u) => (
                                <li key={u.id}>
                                    <button onClick={() => openUser(u.id)}
                                        data-testid={`user-row-${u.id}`}
                                        className={`w-full text-left p-4 transition hover:bg-muted ${selected === u.id ? "bg-muted" : ""}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary/40 text-primary font-heading font-extrabold text-xs">
                                                {(u.name || u.email).slice(0, 2).toUpperCase()}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-bold text-primary truncate">{u.name || "—"}</div>
                                                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                            </div>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_CLASS[u.role || "prospect"]}`}>
                                                {ROLE_LABEL[u.role || "prospect"]}
                                            </span>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="lg:col-span-3">
                    {!selected && (
                        <div className="rounded-2xl border-2 border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground">
                            Seleccione um utilizador para ver detalhes.
                        </div>
                    )}
                    {selected && !detail && <div className="text-sm text-muted-foreground">A carregar detalhe...</div>}
                    {selected && detail && (
                        <div className="space-y-4" data-testid="user-detail">
                            <div className="rounded-2xl border border-border bg-white p-6">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <h3 className="font-heading text-xl font-extrabold text-primary">{detail.user.name || "—"}</h3>
                                        <p className="text-sm text-muted-foreground">{detail.user.email}</p>
                                    </div>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_CLASS[detail.user.role || "prospect"]}`}>
                                        {ROLE_LABEL[detail.user.role || "prospect"]}
                                    </span>
                                </div>
                                <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                                    <Field icon={Mail} label="Email" value={detail.user.email} />
                                    <Field icon={Phone} label="Telefone" value={detail.user.phone || "—"} />
                                    <Field icon={Building2} label="Empresa" value={detail.user.company || "—"} />
                                    <Field icon={FileDigit} label="NIF" value={detail.user.nif || "—"} />
                                </dl>

                                <div className="mt-5 flex flex-wrap gap-2">
                                    {detail.user.role !== "customer" && (
                                        <button onClick={() => changeRole(detail.user.id, "customer")} data-testid="promote-customer"
                                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700">
                                            <UserCheck className="h-3.5 w-3.5" /> Promover a Cliente
                                        </button>
                                    )}
                                    {detail.user.role !== "prospect" && detail.user.role !== "admin" && (
                                        <button onClick={() => changeRole(detail.user.id, "prospect")} data-testid="demote-prospect"
                                            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 hover:bg-amber-100">
                                            <UserX className="h-3.5 w-3.5" /> Despromover a Prospect
                                        </button>
                                    )}
                                    {detail.user.role !== "admin" && (
                                        <button onClick={() => changeRole(detail.user.id, "admin")} data-testid="promote-admin"
                                            className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/20">
                                            <Shield className="h-3.5 w-3.5" /> Tornar Admin
                                        </button>
                                    )}
                                    <button onClick={() => deleteUser(detail.user.id)} data-testid="delete-user"
                                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100">
                                        <Trash2 className="h-3.5 w-3.5" /> Eliminar conta
                                    </button>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-border bg-white p-6">
                                <h4 className="font-heading text-base font-extrabold text-primary flex items-center gap-2"><Package className="h-4 w-4" /> Equipamentos ({detail.equipments.length})</h4>
                                {detail.equipments.length === 0 && <p className="mt-3 text-xs text-muted-foreground">Sem equipamentos atribuídos.</p>}
                                {detail.equipments.length > 0 && (
                                    <ul className="mt-3 space-y-2">
                                        {detail.equipments.map((eq) => (
                                            <li key={eq.id} className="flex items-center justify-between rounded-md border border-border bg-muted px-3 py-2">
                                                <div>
                                                    <div className="text-sm font-bold text-primary">{eq.nickname || eq.serial_number}</div>
                                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{eq.model} · {eq.serial_number}</div>
                                                </div>
                                                <span className="text-[10px] uppercase tracking-wider font-bold text-primary/70">{eq.status}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="rounded-2xl border border-border bg-white p-6">
                                <h4 className="font-heading text-base font-extrabold text-primary flex items-center gap-2"><Wrench className="h-4 w-4" /> Pedidos de assistência ({detail.service_requests.length})</h4>
                                {detail.service_requests.length === 0 && <p className="mt-3 text-xs text-muted-foreground">Sem pedidos.</p>}
                                {detail.service_requests.length > 0 && (
                                    <ul className="mt-3 space-y-2">
                                        {detail.service_requests.map((sr) => (
                                            <li key={sr.id} className="rounded-md border border-border bg-muted px-3 py-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="text-sm font-bold text-primary">{sr.title}</div>
                                                    <span className="text-[10px] uppercase tracking-wider font-bold text-primary/70">{sr.status}</span>
                                                </div>
                                                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{sr.description}</p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 text-primary">
            <Icon className="h-3.5 w-3.5 text-primary/60" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground w-20">{label}</span>
            <span className="font-medium truncate">{value}</span>
        </div>
    );
}
